import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, HStack, Text } from '@chakra-ui/react';

type AppVersion = {
  buildId?: string;
  builtAt?: string;
  version?: string;
};

const VERSION_URL = '/app-version.json';

const fetchAppVersion = async (): Promise<AppVersion | null> => {
  try {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) return null;
    return (await response.json()) as AppVersion;
  } catch {
    return null;
  }
};

const AppUpdateNotice: React.FC = () => {
  const firstBuildIdRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkForUpdate = async (canShowNotice: boolean) => {
      const version = await fetchAppVersion();
      if (cancelled || !version?.buildId) return;

      if (!firstBuildIdRef.current) {
        firstBuildIdRef.current = version.buildId;
        return;
      }

      if (canShowNotice && version.buildId !== firstBuildIdRef.current) {
        setUpdateAvailable(true);
      }
    };

    checkForUpdate(false);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate(true);
      }
    };

    const handleFocus = () => {
      checkForUpdate(true);
    };

    const handlePageShow = () => {
      checkForUpdate(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <Box
      position="fixed"
      right={{ base: 3, md: 5 }}
      bottom={{ base: 3, md: 5 }}
      zIndex={2000}
      bg="rgba(17,24,39,0.94)"
      color="white"
      border="1px solid"
      borderColor="whiteAlpha.300"
      rounded="lg"
      boxShadow="0 18px 46px rgba(0,0,0,0.34)"
      px={{ base: 3, md: 4 }}
      py={3}
      maxW={{ base: 'calc(100vw - 24px)', md: '420px' }}
      backdropFilter="blur(14px)"
    >
      <HStack spacing={3} align="center">
        <Text fontSize="sm" fontWeight="750" lineHeight="1.35" flex="1">
          A new version is available.
        </Text>
        <Button
          size="sm"
          bg="#F6AD55"
          color="#071321"
          fontWeight="850"
          rounded="full"
          onClick={() => window.location.reload()}
          _hover={{ bg: '#F8BC72' }}
        >
          Refresh
        </Button>
      </HStack>
    </Box>
  );
};

export default AppUpdateNotice;
